import { Fragment, useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Nav from "../Component/Nav";
import SideBar from "./partials/SideBar";
import API from "../Network/API";
import { Link } from "react-router-dom";

function UpdateProfile() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: null
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [preview, setPreview] = useState(null);

  // Load current user data on component mount
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const response = await API.get('/check_user');
      if (response && response.data) {
        const userData = response.data?.data ?? response.data;
        // Debug: verify incoming payload shape during development
        // console.log('Loaded user profile:', userData);
        setFormData({
          name: userData?.name ?? '',
          email: userData?.email ?? '',
          phone: userData?.phone ?? '',
          avatar: null
        });
        if (userData.avatar) {
          setPreview(userData.avatar);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        avatar: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      
      if (formData.avatar) {
        submitData.append('avatar', formData.avatar);
      }

      const response = await API.post('/update-profile', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        // Reload profile data to show updated information
        loadUserProfile();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'An error occurred while updating profile' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Nav />
      <div className="studentDashboard">
        <div className="padding">
          <div className="container">
            <div className="row">
              <div className="col-12 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3">
                <div className="DashboardSideBar">
                  <SideBar />
                </div>
              </div>
              <div className="col-12 col-sm-8 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                <div className="StudentDashboardContent">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <span>Welcome to</span>
                      <h4>Update Profile</h4>
                    </div>
                    <Link to="/dashboard" className="btn btn-outline-secondary">
                      <i className="fas fa-arrow-left me-2"></i>Back to Dashboard
                    </Link>
                  </div>

                  <div className="card bg-light-white">
                    <div className="card-body">
                      {message.text && (
                        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} alert-dismissible fade show`} role="alert">
                          {message.text}
                          <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
                        </div>
                      )}

                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-12 mb-4">
                            <div className="text-center">
                              <div className="position-relative d-inline-block">
                                {preview ? (
                                  <img 
                                    src={preview} 
                                    alt="Profile Preview" 
                                    className="rounded-circle mb-3" 
                                    style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div 
                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center mb-3" 
                                    style={{ width: '120px', height: '120px' }}
                                  >
                                    <i className="fas fa-user fa-3x text-muted"></i>
                                  </div>
                                )}
                                <label 
                                  htmlFor="avatar" 
                                  className="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-circle"
                                  style={{ width: '35px', height: '35px' }}
                                >
                                  <i className="fas fa-camera"></i>
                                </label>
                                <input
                                  type="file"
                                  id="avatar"
                                  name="avatar"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                  className="d-none"
                                />
                              </div>
                              <p className="text-muted small">Click the camera icon to upload a new profile picture</p>
                            </div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="name" className="form-label">Full Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              disabled={loading}
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="email" className="form-label">Email Address *</label>
                            <input
                              type="email"
                              className="form-control"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              readOnly
                              disabled={loading}
                              style={{ backgroundColor: '#f5f5f5' }}
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label htmlFor="phone" className="form-label">Phone Number</label>
                            <input
                              type="tel"
                              className="form-control"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              disabled={loading}
                              placeholder="+1234567890"
                            />
                          </div>

                          <div className="col-12 mt-4">
                            <div className="d-flex gap-2">
                              <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                              >
                                {loading ? (
                                  <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Updating...
                              </>
                                ) : (
                                  'Update Profile'
                                )}
                              </button>
                              <Link to="/dashboard" className="btn btn-outline-secondary">
                                Cancel
                              </Link>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-3">
                <div className="dashboardEvent">
                  <h6>Profile Tips</h6>
                  <div className="card bg-light-white">
                    <div className="card-body">
                      <ul className="list-unstyled mb-0">
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Use a clear profile picture
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Keep your information up to date
                        </li>
                        <li className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Use a valid email address
                        </li>
                        <li>
                          <i className="fas fa-check-circle text-success me-2"></i>
                          Add a phone number for better communication
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </Fragment>
  );
}

export default UpdateProfile;
